import type { SemanticEventBus } from '../events/semantic-event-bus';
import type { PlayerId } from '../../shared/types/ids';
import type { BodyZone, ThriveInputEvent } from '../../shared/types/input-events';
import type { GameplayTunables } from '../../shared/types/tunables';
import { DEFAULT_GAMEPLAY_TUNABLES } from '../../platform/config/tunables';

/**
 * Keyboard input adapter (D-006, PLAN Phase 2 mapping).
 *
 * It translates key presses into exactly the same semantic events a pose detector will emit,
 * so keyboard and camera sessions share one game-logic path. It contains no game logic of its
 * own: it never decides whether an answer is right, and never touches scoring or storage.
 *
 * One adapter serves one hero. The key set comes from the hero's id, so a stage that seats
 * Hero 2 cannot hand them Hero 1's keys by forgetting an argument:
 *
 *   Hero 1:  A / D                left / right lane
 *            S                    centre lane, or crouch — by context
 *            W                    hands raised (held)
 *            Space                jump / count action (debounced)
 *
 *   Hero 2:  Left / Right Arrow   left / right lane
 *            Down Arrow           centre lane, or crouch — by context
 *            Up Arrow             hands raised (held)
 *            Enter                jump / count action (debounced)
 *
 * Both heroes emit the same event types; only `playerId` differs, so nothing downstream needs to
 * know which child produced an answer in order to understand it.
 *
 * The centre key is the "centre-or-crouch by context" key D-006 describes. Which of the two it
 * means is not something the adapter can work out — a keypress carries no mechanic — so the
 * mission runtime sets it per mechanic through `setInputContext`, mirroring how the same body
 * posture means "centre lane" in Portal Pick and "no" in Spider-Sense Factory. It defaults to
 * `ZONES`, the mechanic type most of the mission uses. Only the centre key changes meaning: the
 * side keys keep their lanes in both contexts (D-035).
 *
 * **Freeze, repeat and pause are not here.** They belong to the sitting rather than to either
 * child (D-045 PS2-5) and live in `session-controls.ts`; two heroes' adapters both binding F
 * would toggle a freeze twice per press, and a per-hero freeze is not a stillness test. This
 * adapter only *reads* the session's freeze gate, and goes silent while it is on.
 *
 * Held state is cleared on window blur, on the tab being hidden, on the freeze lifting, and on a
 * context switch, because the browser never delivers the matching keyup in those cases.
 */

export const HERO_1_PLAYER_ID: PlayerId = 'hero-1';

/**
 * The second hero's session player id (Phase 2 Slice A).
 *
 * It identifies the right-lane slot for the whole mission — resume matching, participation and
 * evidence attribution all key off it — and it is also what chooses this hero's key set.
 */
export const HERO_2_PLAYER_ID: PlayerId = 'hero-2';

/**
 * What the centre key currently means.
 *
 * `ZONES` — three-lane mechanics (Portal Pick, Boss Battle): it is the centre lane.
 * `BINARY` — yes/no posture mechanics (Spider-Sense Factory): it is a crouch, and the hands key
 * is the other answer.
 */
export type KeyboardInputContext = 'ZONES' | 'BINARY';

export const KEYBOARD_INPUT_CONTEXTS: readonly KeyboardInputContext[] = [
  'ZONES',
  'BINARY',
] as const;

export const DEFAULT_KEYBOARD_INPUT_CONTEXT: KeyboardInputContext = 'ZONES';

/** One hero's key set. Every key an adapter consumes is named here and nowhere else. */
export interface KeyboardHeroBindings {
  /** Lane keys, by `KeyboardEvent.code`. The centre lane's key is also the crouch key. */
  readonly zones: Readonly<Record<string, BodyZone>>;
  readonly hands: string;
  readonly action: string;
  /** The context-dependent key: centre lane in ZONES, crouch in BINARY. */
  readonly centreOrCrouch: string;
}

export const HERO_1_KEYBOARD_BINDINGS: KeyboardHeroBindings = {
  zones: { KeyA: 'left', KeyS: 'center', KeyD: 'right' },
  hands: 'KeyW',
  action: 'Space',
  centreOrCrouch: 'KeyS',
};

/**
 * Hero 2's key set, transcribed from PHASED_IMPLEMENTATION_PLAN.md's Phase 2 mapping (D-006).
 *
 * Note which arrow does what: **Up Arrow is hands raised**, mirroring Hero 1's W, and **Enter**
 * is the jump/count action, mirroring Space. Up is not a jump key — reading it as one would give
 * the two children different answers to the same posture question.
 */
export const HERO_2_KEYBOARD_BINDINGS: KeyboardHeroBindings = {
  zones: { ArrowLeft: 'left', ArrowDown: 'center', ArrowRight: 'right' },
  hands: 'ArrowUp',
  action: 'Enter',
  centreOrCrouch: 'ArrowDown',
};

/**
 * The hero's key set, chosen by their id.
 *
 * Deriving it rather than passing it is what stops a two-hero stage seating Hero 2 with Hero 1's
 * keys: there is no argument to get wrong. Anyone else (a third id could only come from a future
 * mode) plays on Hero 1's keys, which is the safe reading — never silently unbound.
 */
export function keyboardBindingsFor(playerId: PlayerId): KeyboardHeroBindings {
  return playerId === HERO_2_PLAYER_ID ? HERO_2_KEYBOARD_BINDINGS : HERO_1_KEYBOARD_BINDINGS;
}

/**
 * The sitting's freeze, read by every adapter on the stage (D-045 PS2-5).
 *
 * There is exactly one of these per stage and the adapters do not own it, which is what makes
 * "one F silences everybody" true by construction rather than by both adapters agreeing.
 */
export interface KeyboardFreezeGate {
  readonly frozen: boolean;
  /** Called on every change. Adapters use it to retract what they believe is held. */
  subscribe(listener: (frozen: boolean) => void): () => void;
}

export interface KeyboardInputAdapterOptions {
  bus: SemanticEventBus;
  /** Which hero this adapter belongs to. It also chooses the key set. */
  playerId?: PlayerId;
  /** Defaults to `window`; injected in tests. */
  target?: EventTarget | null;
  tunables?: GameplayTunables;
  /** Injected in tests so debounce behaviour is deterministic. */
  now?: () => number;
  /**
   * The sitting's freeze. Without one the adapter is never frozen — freeze is a session control,
   * and an adapter built outside a session has no session to be frozen by.
   */
  freeze?: KeyboardFreezeGate;
  /** Starting meaning of the centre key; the mission runtime changes it per mechanic. */
  inputContext?: KeyboardInputContext;
}

export interface KeyboardInputAdapter {
  attach(): void;
  detach(): void;
  readonly attached: boolean;
  /** Which hero this adapter speaks for. */
  readonly playerId: PlayerId;
  /** Keys this adapter consumes, for the shell's on-screen help. */
  readonly boundKeys: readonly string[];
  readonly inputContext: KeyboardInputContext;
  /**
   * Switches what the centre key means. Anything currently held is released first — a child who
   * is holding a crouch when the mechanic changes must not leave a crouch latched behind them.
   */
  setInputContext(context: KeyboardInputContext): void;
  /** True while the sitting's freeze is suppressing emission. */
  readonly frozen: boolean;
}

export function createKeyboardInputAdapter(
  options: KeyboardInputAdapterOptions,
): KeyboardInputAdapter {
  const {
    bus,
    playerId = HERO_1_PLAYER_ID,
    tunables = DEFAULT_GAMEPLAY_TUNABLES,
    now = () => Date.now(),
    freeze,
  } = options;

  const bindings = keyboardBindingsFor(playerId);

  const target =
    options.target === undefined ? (typeof window === 'undefined' ? null : window) : options.target;

  let attached = false;
  let currentZone: BodyZone | null = null;
  let handsRaised = false;
  let crouched = false;
  let unsubscribeFreeze: (() => void) | null = null;
  let inputContext: KeyboardInputContext = options.inputContext ?? DEFAULT_KEYBOARD_INPUT_CONTEXT;
  let lastJumpAt = Number.NEGATIVE_INFINITY;

  const emit = (event: ThriveInputEvent): void => {
    bus.emit(event);
  };

  // Space must still activate a focused button, and typing a name must stay typing a name.
  const isEditingOrPressingControl = (event: KeyboardEvent): boolean => {
    const target = event.target;
    if (target === null || !(typeof HTMLElement !== 'undefined' && target instanceof HTMLElement)) {
      return false;
    }
    if (target.isContentEditable) return true;
    return ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'].includes(target.tagName);
  };

  /** True while the sitting is frozen. Owned by the session controls, never by this adapter. */
  const isFrozen = (): boolean => freeze?.frozen ?? false;

  /** Every key this hero's adapter consumes, whatever the current context. */
  const isBoundKey = (code: string): boolean =>
    code in bindings.zones || code === bindings.hands || code === bindings.action;

  const handleKeyDown = (event: Event): void => {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.repeat) return;
    if (isEditingOrPressingControl(keyboardEvent)) return;
    if (!isBoundKey(keyboardEvent.code)) return;

    keyboardEvent.preventDefault();

    // A frozen stage is deliberately silent — that is what makes it a stillness test.
    if (isFrozen()) return;

    if (inputContext === 'BINARY' && keyboardEvent.code === bindings.centreOrCrouch) {
      if (crouched) return;
      crouched = true;
      emit({ type: 'PLAYER_CROUCHED', playerId, at: now() });
      return;
    }

    const zone = bindings.zones[keyboardEvent.code];
    if (zone !== undefined) {
      // Re-pressing the key for the zone you are already in is not a new answer.
      if (zone === currentZone) return;
      currentZone = zone;
      emit({ type: 'PLAYER_ZONE_CHANGED', playerId, zone, at: now() });
      return;
    }

    if (keyboardEvent.code === bindings.hands) {
      if (handsRaised) return;
      handsRaised = true;
      emit({ type: 'PLAYER_RAISED_HANDS', playerId, at: now() });
      return;
    }

    if (keyboardEvent.code === bindings.action) {
      const at = now();
      if (at - lastJumpAt < tunables.detector.actionDebounceMs) return;
      lastJumpAt = at;
      emit({ type: 'PLAYER_JUMPED', playerId, at });
    }
  };

  const handleKeyUp = (event: Event): void => {
    const keyboardEvent = event as KeyboardEvent;
    const isCrouchRelease =
      inputContext === 'BINARY' && keyboardEvent.code === bindings.centreOrCrouch && crouched;
    if (keyboardEvent.code !== bindings.hands && !isCrouchRelease) return;
    // Note: unlike keydown, a keyup landing on a form control is still honoured. Swallowing
    // it is how hands get stuck raised when focus moves mid-press.
    if (!isEditingOrPressingControl(keyboardEvent)) keyboardEvent.preventDefault();
    if (isFrozen()) return;

    if (isCrouchRelease) {
      releaseCrouch();
      return;
    }
    releaseHands();
  };

  function releaseHands(): void {
    if (!handsRaised) return;
    handsRaised = false;
    emit({ type: 'PLAYER_LOWERED_HANDS', playerId, at: now() });
  }

  function releaseCrouch(): void {
    if (!crouched) return;
    crouched = false;
    emit({ type: 'PLAYER_STOOD', playerId, at: now() });
  }

  /**
   * Clears everything the adapter believes is being held, emitting the events that say so.
   *
   * The browser does not deliver keyup when the window loses focus mid-press, so without this
   * a child who alt-tabs (or clicks a control) comes back with hands stuck raised and the next
   * W press ignored — the action is dead until the whole adapter remounts.
   *
   * While the sitting is frozen it does **not** emit (R1-10). A frozen stage is silent all the
   * way through — that is the whole of the stillness test — and a blur, a hidden tab or a change
   * of mechanic are none of them a child moving. Emitting anyway broke the gate twice over: the
   * stage spoke while it was supposed to be deaf, and having already retracted, the release the
   * freeze-lift owes each held posture had nothing left to release. So the retraction is deferred
   * and flushed when the freeze lifts, which is the moment the stage is allowed to speak again.
   *
   * The one exception is `detach()`, which passes `force`: an adapter that is going away would
   * otherwise be silent for ever, and would leave a posture latched behind it (D-048(2)).
   */
  function releaseHeldKeys(options: { force?: boolean } = {}): void {
    if (isFrozen() && options.force !== true) {
      // Nothing to remember: the state stays exactly as held, and the lift retracts it then.
      return;
    }
    releaseHands();
    releaseCrouch();
    // Zone is a latched position rather than a held key, but the same reasoning applies: after
    // a focus break the next press must be treated as a fresh choice.
    currentZone = null;
  }

  const handleBlur = (): void => {
    releaseHeldKeys();
  };

  const handleVisibilityChange = (): void => {
    if (typeof document === 'undefined' || document.visibilityState !== 'hidden') return;
    releaseHeldKeys();
  };

  return {
    get attached() {
      return attached;
    },

    playerId,

    get frozen() {
      return isFrozen();
    },

    get inputContext() {
      return inputContext;
    },

    setInputContext(context: KeyboardInputContext): void {
      if (context === inputContext) return;
      // Release under the *old* meaning first: after the switch, S is a different event and
      // the crouch (or the latched centre zone) would never be retracted. Frozen, this defers
      // like every other retraction (R1-10) — and the crouch still retracts as a crouch, because
      // nothing about the held state changes while the deferral holds.
      releaseHeldKeys();
      inputContext = context;
    },

    boundKeys: [...Object.keys(bindings.zones), bindings.hands, bindings.action],

    attach(): void {
      if (attached || target === null) return;
      attached = true;
      target.addEventListener('keydown', handleKeyDown);
      target.addEventListener('keyup', handleKeyUp);
      target.addEventListener('blur', handleBlur);
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', handleVisibilityChange);
      }
      // Coming out of a freeze, nothing this adapter believes is held can be trusted: the
      // matching keyups may have happened while the whole stage was deaf.
      unsubscribeFreeze =
        freeze?.subscribe((frozen) => {
          if (!frozen) releaseHeldKeys();
        }) ?? null;
      // A keyboard player is always "in frame"; presence still flows through the same event.
      emit({ type: 'PLAYER_ENTERED_FRAME', playerId, at: now() });
    },

    detach(): void {
      if (!attached || target === null) return;
      attached = false;
      target.removeEventListener('keydown', handleKeyDown);
      target.removeEventListener('keyup', handleKeyUp);
      target.removeEventListener('blur', handleBlur);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
      unsubscribeFreeze?.();
      unsubscribeFreeze = null;
      // Detaching while frozen must still retract what is held: the adapter is going away, so
      // "silent until unfrozen" would mean "silent for ever". The gate is left alone — it belongs
      // to the sitting, and one hero leaving is not a reason to unfreeze the other.
      // Forced: a departing adapter cannot wait for a freeze that may never lift (D-048(2)).
      releaseHeldKeys({ force: true });
      lastJumpAt = Number.NEGATIVE_INFINITY;
      emit({ type: 'PLAYER_LEFT_FRAME', playerId, at: now() });
    },
  };
}
